<template>
<div class="px-1 pt-1">
  <div class="row m-0">
    <div class="col-auto">
      <h2>{{ $t('page.stockReceipt.form.title') }}</h2>
    </div>
    <div class="col">
      <div class="form-check m-2">
        <input class="form-check-input" type="checkbox" v-model="requestQuantityInput" id="requestQuantityInput">
        <label class="form-check-label" for="requestQuantityInput">{{ $t('action.requestQuantityInput') }}</label>
      </div>
    </div>
  </div>
  <ScanInput ref="scanInput" :instruction="$t('action.scanBarcode')" @scanned="barcodeScanned" class="input-group" />

  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>

  <form @submit.prevent="postReceipt" v-if="currentItem" :disabled="isLoading" class="pe-1">
    <div class="fs-4 d-flex mt-2">
      <ItemField :item="currentItem" />
    </div>

    <div v-if="scanResultIncomplete" class="row g-3">

      <div class="col-sm-6 col-md-4 col-lg-3">
        <label for="quantity" class="form-label d-flex align-items-center mb-1">{{ $t('field.generic.quantity') }} <div class="text-primary"><UnitQuantity :quantity="currentStockQuantity" :unit-name="currentItem?.baseUnitCode" :inline-unit-name="true" /></div></label>
        <div class="input-group">
          <input v-model="currentQuantity" type="number" min="1" max="10000" class="form-control" id="quantity" ref="quantityInput" :class="{ 'is-invalid': !isQuantityValid }">
          <span class="input-group-text">{{ currentItem?.baseUnitCode }}</span>
        </div>
      </div>

      <div v-if="currentItem?.trackLotNumber" class="col-sm-6 col-md-4 col-lg-3">
        <label for="lot" class="form-label">{{ $t('field.masterdata.lot') }}</label>
        <input v-model="currentLot" type="text" class="form-control" id="lot" ref="lotInput" :class="{ 'is-invalid': !isLotValid }">
      </div>

      <div v-if="currentItem?.trackBestBeforeDate" class="col-sm-6 col-md-4 col-lg-3">
        <label for="bestBefore" class="form-label">{{ $t('field.masterdata.bestBefore') }}</label>
        <input v-model="currentBestBefore" type="date" class="form-control" id="bestBefore" ref="bestBeforeInput" :min="new Date().toISOString().split('T')[0]" :class="{ 'is-invalid': !isBestBeforeValid }">
      </div>

      <div v-if="currentItem?.trackExpirationDate" class="col-sm-6 col-md-4 col-lg-3">
        <label for="expiry" class="form-label">{{ $t('field.masterdata.expiry') }}</label>
        <input v-model="currentExpiry" type="date" class="form-control" id="expiry" ref="expiryInput" :min="new Date().toISOString().split('T')[0]" :class="{ 'is-invalid': !isExpiryValid }">
      </div>

      <div class="d-grid gap-2">
        <button type="submit" class="btn btn-primary btn-lg d-block" :disabled="!isValid || isLoading"><font-awesome-icon :icon="['fas', 'plus']" />  {{ $t('page.stockReceipt.form.confirm') }}</button>
      </div>

    </div>
  </form>
</div>
</template>

<script setup lang="ts">
import ScanInput from "@/components/form/ScanInput.vue";
import { computed, onMounted, ref, nextTick } from "vue";
import { storeToRefs } from 'pinia';
import api from "@/services/api";
import IGS1 from "@/models/IGS1";
import { useSessionStore, useUserSettingsStore, useItemsStore, useTradeItemsStore, useStockStore } from "@/stores";
import { toast } from "vue3-toastify";
import IncreaseStockRequest from "@/models/requests/IIncreaseStockRequest";
import { useI18n } from "vue-i18n";
import { ItemField } from '@/components/fields';
import UnitQuantity from "@/components/UnitQuantity.vue";
import { AxiosError } from "axios";
import Item from "@/models/frontend/Item";

const userSettingsStore = useUserSettingsStore();
const { data: userSettings } = storeToRefs(userSettingsStore);
const i18n = useI18n();

const isLoading = ref(false);
const error = ref('');
const scanResultIncomplete = ref(false);
const requestQuantityInput = ref(false);

const currentItem = ref<Item | undefined>();
const currentQuantity = ref<number | undefined>();
const currentLot = ref<string | undefined>();
const currentBestBefore = ref<string | undefined>();
const currentExpiry = ref<string | undefined>();
const currentStockQuantity = ref<number>(0);

const scanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const quantityInput = ref<HTMLInputElement | null>(null);
const lotInput = ref<HTMLInputElement | null>(null);
const bestBeforeInput = ref<HTMLInputElement | null>(null);
const expiryInput = ref<HTMLInputElement | null>(null);

const itemStore = useItemsStore();
const tradeItemStore = useTradeItemsStore();
const stockStore = useStockStore();
const { error: stockError } = storeToRefs(stockStore);

const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);

onMounted(async () => {
  requestQuantityInput.value = userSettings.value.confirmQuantity ?? false;
});

const isQuantityValid = computed(() => {
  if (!currentQuantity.value) { return false; }
  return currentQuantity.value > 0;
});

const isLotValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackLotNumber) { return true; }
  return !!currentLot.value;
});

const isBestBeforeValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackBestBeforeDate) { return true; }
  if (!currentBestBefore.value) { return false; }
  return (new Date(currentBestBefore.value) >= new Date());
});

const isExpiryValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackExpirationDate) { return true; }
  if (!currentExpiry.value) { return false; }
  return (new Date(currentExpiry.value) >= new Date());
});

const isValid = computed(() => {
  if (!isQuantityValid.value) { return false; }
  if (!isLotValid.value) { return false; }
  if (!isBestBeforeValid.value) { return false; }
  if (!isExpiryValid.value) { return false; }
  return true;
});

const getDateString = (date: Date | undefined) => {
  if (!date) { return undefined; }
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - (offset * 60 * 1000));
  return date.toISOString().split('T')[0];
}

const barcodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  scanResultIncomplete.value = true; // used to show/hide the form fields after successful scanning, but not the stock display for current item)
  currentItem.value = undefined;
  currentQuantity.value = 1;
  currentLot.value = undefined;
  currentBestBefore.value = undefined;
  currentExpiry.value = undefined;
  let nextErrorField = '';

  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    var tradeItem = tradeItemStore.getByCode(contentCode);
    if (tradeItem) {
      currentItem.value = itemStore.getById(tradeItem.itemId);
    }
    if (!currentItem.value && gs1?.itemCode) {
      currentItem.value = itemStore.getByCode(gs1.itemCode);
    }
  }

  // if not matched already, try get item by code
  if (!currentItem.value) {
    currentItem.value = itemStore.getByCode(barcode);
  }

  if (!currentItem.value) {
    error.value = i18n.t('error.unknownItem') + ': "' + barcode + '"';
    return;
  }

  error.value = '';

  if (gs1?.quantity) {
    currentQuantity.value = gs1.quantity * currentQuantity.value;
  }

  if (requestQuantityInput.value) {
    // this is required to invalidate the form (disabled submit button)
    currentQuantity.value = 0;
  }

  if (currentItem.value?.trackLotNumber) {
    if (gs1?.lot) {
      currentLot.value = gs1.lot;
    }
    if (!isLotValid.value && !nextErrorField) {
      nextErrorField = 'lot';
    }
  }

  if (currentItem.value.trackBestBeforeDate) {
    if (gs1?.bestBefore) {
      currentBestBefore.value = getDateString(gs1.bestBefore);
    }
    if (!isBestBeforeValid.value && !nextErrorField) {
      nextErrorField = 'bestBefore';
    }
  }

  if (currentItem.value.trackExpirationDate) {
    if (gs1?.expiry) {
      currentExpiry.value = getDateString(gs1.expiry);
    }
    if (!isExpiryValid.value && !nextErrorField) {
      nextErrorField = 'expiry';
    }
  }

  // if we did not have any error fields, directly post the receipt now
  if (!nextErrorField) {
    if (requestQuantityInput.value) {
      scanResultIncomplete.value = true;
      await fetchCurrentStockQuantity();
      await nextTick(() => {
        const qIn = quantityInput.value;
        if (qIn) qIn.value = "";
        quantityInput.value?.select();
      });
      return;
    }
    await postReceipt();
  }
  else {
    scanResultIncomplete.value = true;
    await fetchCurrentStockQuantity();
    await nextTick(() => {
      if (nextErrorField === 'quantity') quantityInput.value?.select();
      else if (nextErrorField === 'lot') lotInput.value?.focus();
      else if (nextErrorField === 'bestBefore') bestBeforeInput.value?.focus();
      else if (nextErrorField === 'expiry') expiryInput.value?.focus();
    });
  }
};

const fetchCurrentStockQuantity = async () => {
  if (!currentItem.value) { return; }
  await api
    .get<number>(`v1.0/stocks/.qty?siteId=${selectedSiteId.value}&itemId=${currentItem.value.id}`)
    .then((response) => {
      // currentStockQuantity.value = response.data.reduce((acc, stock) => acc + stock.quantity, 0);
      currentStockQuantity.value = response.data;
    })
    .catch((error) => (error = { error }));
}

const postReceipt = async () => {
  scanResultIncomplete.value = false;
  requestQuantityInput.value = userSettings.value.confirmQuantity ?? false;
  isLoading.value = true;

  const request: IncreaseStockRequest = {
    siteId: selectedSiteId.value,
    itemId: currentItem.value!.id,
    quantity: currentQuantity.value!,
    lot: currentLot.value,
    bestBefore: currentBestBefore.value,
    expiry: currentExpiry.value
  };

  await stockStore.postStockIncrease(request);
  await fetchCurrentStockQuantity();

  if (stockError.value instanceof AxiosError)
  {
    const axiosError = stockError.value as AxiosError;
    toast.error(i18n.t('error.axiosErrorMessage', { code: axiosError.code, message: axiosError.message }));
  }
  else if (stockError.value)
  {
    toast.error(i18n.t('page.stockReceipt.errorMessage', { error: stockError.value }));
  }
  else {
    toast.info(i18n.t('page.stockReceipt.successMessage', { quantity: currentQuantity.value, item: currentItem.value?.description, unit: currentItem.value?.baseUnitCode }));
  }

  isLoading.value = false;
  scanInput.value?.focus();
}
</script>
